/* ============================================================
   VITALCASA CRM — ModalWhatsApp (tema claro/oscuro)
   Modal para envío de WhatsApp desde el CRM.
   
   Dos modos:
   - Con propiedades seleccionadas: muestra las propiedades adjuntas
   - Mensaje directo o con propiedades: texto libre + mismas respuestas rápidas (intro + fichas si hay props)
   
   Al enviar: abre wa.me en popup pequeña (no redirige)
   y marca las propiedades como enviadas en la BD.
   
   Props:
   - isOpen: boolean
   - onClose: () => void
   - onSend: ({ contacto, propiedades, mensaje }) => void
   - contact: { nombre, telefono, iniciales }
   - properties?: [{ id, titulo, detalle, precio, icono }]
   ============================================================ */

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from './ThemeProvider';
import api from '../services/api';
import { buildWhatsAppQuickReplies, normalizeWaIdioma } from '../utils/whatsappQuickReplies';
import { LEAD_FLOW_TIMING } from '../constants/leadFlowOutcomes';
import { mostrarToastEnvioPropiedades, mostrarToastVitalCasa } from '../utils/mostrarToastVitalCasa';
import {
  aplicarPlaceholdersMensaje,
  nombreParaPlantillaMensaje,
  primerNombre,
} from '../utils/nombrePlantillaMensaje';

function formatPlazoMinutos(min) {
  const n = Number(min) || 0;
  if (n >= 60 && n % 60 === 0) return `${n / 60} h`;
  return `${n} min`;
}

const WA_BLOCKED_EXTEND_LABEL = formatPlazoMinutos(
  LEAD_FLOW_TIMING.whatsappWaitExtendMinutes || LEAD_FLOW_TIMING.defaultTaskDueMinutes || 240
);

// ── Colores WhatsApp (fijos en ambos temas) ─────────────────
const wa = {
  green:       '#16a34a',
  greenFrom:   '#15803d',
  greenTo:     '#16a34a',
  greenLight:  '#22c55e',
  greenText:   '#bbf7d0',
  greenShadow: 'rgba(22,163,74,0.35)',
  greenBg:     'rgba(22,163,74,0.08)',
  greenBorder: 'rgba(22,163,74,0.20)',
  chipText:    '#4ade80',
};

const WA_RETRY_WAIT_MS = 25_000;
const WA_MAX_SEND_RETRIES = 2;
/** Tope por intento: si el backend cuelga (p. ej. descarga foto), el modal no se queda en «Enviando…». */
const WA_FETCH_TIMEOUT_MS = 90_000;

async function fetchJsonWa(url, options = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), WA_FETCH_TIMEOUT_MS);
  let resp;
  let text = '';
  try {
    resp = await fetch(url, { ...options, signal: controller.signal });
    text = await resp.text();
  } catch (e) {
    const aborted = e?.name === 'AbortError' || /aborted/i.test(String(e?.message || ''));
    return {
      ok: false,
      error: aborted
        ? 'El envío ha tardado demasiado. Comprueba WhatsApp en Configuración e inténtalo de nuevo.'
        : (e?.message || 'No se pudo conectar con el servidor'),
      needsConnection: aborted,
      canRetry: aborted,
    };
  } finally {
    clearTimeout(timer);
  }
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    const body = String(text || '').replace(/<[^>]+>/g, ' ').trim();
    const proxyFail =
      [502, 503, 504, 408].includes(resp.status) ||
      /application failed to respond|bad gateway|gateway timeout|cloudflare/i.test(body);
    return {
      ok: false,
      error: proxyFail
        ? 'WhatsApp reconectando tras reinicio del servidor. Espera unos segundos e inténtalo de nuevo.'
        : (resp.ok ? 'Respuesta invalida del servidor' : `Error del servidor (HTTP ${resp.status})`),
      needsConnection: proxyFail,
      canRetry: proxyFail,
    };
  }
  if (!resp.ok && data.ok !== true) {
    data.ok = false;
  }
  if (!data.error && !resp.ok) {
    data.error = data.message || data.msg || `Error del servidor (HTTP ${resp.status})`;
  }
  // Proxy / edge a veces responde JSON con este mensaje
  if (/application failed to respond/i.test(String(data.error || data.message || ''))) {
    data.error = 'WhatsApp reconectando tras reinicio del servidor. Espera unos segundos e inténtalo de nuevo.';
    data.needsConnection = true;
    data.canRetry = true;
  }
  if (!resp.ok && [502, 503, 504, 408].includes(resp.status) && data.canRetry == null) {
    data.canRetry = true;
    data.needsConnection = true;
  }
  return data;
}

async function kickWhatsappReconnect(token) {
  await fetch('/api/whatsapp/conectar', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ forzar_qr: false }),
  }).catch(() => {});
}

async function enviarWaConReintento({ url, options, token }) {
  let lastData = null;
  for (let intento = 0; intento <= WA_MAX_SEND_RETRIES; intento += 1) {
    lastData = await fetchJsonWa(url, options);
    if (lastData.ok) return lastData;
    if (!lastData.canRetry || intento >= WA_MAX_SEND_RETRIES) break;
    await kickWhatsappReconnect(token);
    await new Promise((r) => setTimeout(r, WA_RETRY_WAIT_MS));
  }
  return lastData;
}

function mensajeErrorWhatsapp(data) {
  let err = String(data?.error || data?.message || data?.msg || '').trim();
  if (/No LID for user/i.test(err) || /static\.whatsapp\.net\/rsrc/i.test(err)) {
    err =
      'WhatsApp no pudo abrir el chat con ese número. Reintenta en unos segundos; si sigue, que el destinatario te escriba primero.';
  }
  if (!data?.needsConnection) {
    return err || 'Error enviando WhatsApp';
  }
  if (data.canRetry) {
    return `${err || 'WhatsApp reconectando'}. El servidor está reconectando la sesión; reintenta en un minuto o abre Configuración → WhatsApp.`;
  }
  if (data.needsQr) {
    return `${err || 'WhatsApp no conectado'}. Escanea el QR en Configuración → WhatsApp. El plazo de la tarea se ha ampliado ${WA_BLOCKED_EXTEND_LABEL}.`;
  }
  return `${err || 'WhatsApp no conectado'}. Revisa Configuración → WhatsApp. El plazo de la tarea se ha ampliado ${WA_BLOCKED_EXTEND_LABEL}.`;
}

export default function ModalWhatsApp({ isOpen, onClose, onSend, contact, properties = [], idioma = 'ES', allowManualRecipient = false, quickReplies: customReplies = null, agenteTarjetaUrl = null, crmContext = null }) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === 'dark';

  const [mensaje, setMensaje] = useState('');
  const [propsAdjuntas, setPropsAdjuntas] = useState(properties);
  const [enviado, setEnviado] = useState(false);
  const [msgFocused, setMsgFocused] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [errorEnvio, setErrorEnvio] = useState('');
  const [waSesionOk, setWaSesionOk] = useState(true);
  const [waSesionAviso, setWaSesionAviso] = useState('');
  const [manualTelefono, setManualTelefono] = useState('');
  const [manualNombre, setManualNombre] = useState('');
  const [enviarTarjeta, setEnviarTarjeta] = useState(false);
  const [tarjetaUrl, setTarjetaUrl] = useState(null);
  const [agentePrimerNombre, setAgentePrimerNombre] = useState('');
  /** Catálogo plano de vídeos de las propiedades adjuntas (para el selector de envío). */
  const [videosCatalog, setVideosCatalog] = useState([]);
  const [videosLoading, setVideosLoading] = useState(false);
  /** 'ninguno' | 'todos' | <uuid de propiedad_videos> */
  const [videoSeleccion, setVideoSeleccion] = useState('ninguno');

  const propIdsKey = propsAdjuntas.map(p => p.id).filter(Boolean).sort().join(',');

  // Solo depende de propIdsKey (estable): si incluimos propsAdjuntas, cada re-render del padre
  // cancela Promise.all a medias y el catálogo puede quedarse solo con la 1ª propiedad.
  useEffect(() => {
    if (!isOpen || !propIdsKey) {
      setVideosCatalog([]);
      setVideoSeleccion('ninguno');
      setVideosLoading(false);
      return;
    }
    const snapshot = propsAdjuntas.filter((p) => p?.id);
    let cancelled = false;
    setVideosLoading(true);
    Promise.all(
      snapshot.map((p) =>
        api
          .get(`/propiedades/${p.id}/videos`)
          .then((r) => ({ prop: p, videos: r.data?.data || [] }))
          .catch(() => ({ prop: p, videos: [] })),
      ),
    )
      .then((results) => {
        if (cancelled) return;
        const flat = [];
        for (const { prop: p, videos } of results) {
          const ref = (p.referencia && String(p.referencia).trim()) || '';
          (videos || []).forEach((v, idx) => {
            flat.push({
              ...v,
              propiedad_id: p.id,
              label: `${ref || (p.titulo || 'Prop.').slice(0, 28)} · ${(v.titulo && v.titulo.trim()) || `Vídeo ${idx + 1}`}`,
            });
          });
        }
        setVideosCatalog(flat);
      })
      .finally(() => {
        if (!cancelled) setVideosLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- snapshot al cambiar ids
  }, [isOpen, propIdsKey]);

  useEffect(() => {
    if (!videosCatalog.length) {
      setVideoSeleccion('ninguno');
      return;
    }
    setVideoSeleccion((prev) => {
      if (prev === 'ninguno' || prev === 'todos') return prev;
      return videosCatalog.some((v) => v.id === prev) ? prev : 'ninguno';
    });
  }, [videosCatalog]);

  function videoEnvioPayload() {
    if (videoSeleccion === 'todos') return { modo: 'todos' };
    if (videoSeleccion === 'ninguno' || !videosCatalog.length) return { modo: 'ninguno' };
    return { modo: 'uno', video_id: videoSeleccion };
  }

  /** Nº de vídeos que realmente se enviarán (para las respuestas rápidas). */
  function countVideosSeleccionados() {
    if (videoSeleccion === 'todos') return videosCatalog.length;
    if (videoSeleccion === 'ninguno' || !videoSeleccion) return 0;
    return videosCatalog.some((v) => v.id === videoSeleccion) ? 1 : 0;
  }

  useEffect(() => {
    if (isOpen) {
      setErrorEnvio('');
      setPropsAdjuntas(properties);
      setManualTelefono(contact?.telefono ? String(contact.telefono).replace(/\s/g, '') : '');
      setManualNombre(contact?.nombre || '');
      setEnviarTarjeta(false); // siempre desmarcado al abrir

      // Tarjeta: priorizar la del agente asignado a la captación (prop agenteTarjetaUrl)
      // Si no hay, usar la del usuario logueado
      if (agenteTarjetaUrl) {
        setTarjetaUrl(agenteTarjetaUrl);
      } else {
        // Resetear para no mostrar la tarjeta de un agente anterior
        setTarjetaUrl(null);
        try {
          const stored = JSON.parse(localStorage.getItem('usuario') || '{}');
          if (stored.tarjeta_url) {
            setTarjetaUrl(stored.tarjeta_url);
          } else {
            api.get('/auth/me').then(r => {
              const url = r.data?.tarjeta_url;
              if (url) {
                setTarjetaUrl(url);
                try {
                  const u = JSON.parse(localStorage.getItem('usuario') || '{}');
                  localStorage.setItem('usuario', JSON.stringify({ ...u, tarjeta_url: url }));
                } catch { /* ignorar */ }
              }
            }).catch(() => {});
          }
        } catch { /* ignorar */ }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, agenteTarjetaUrl]);

  // Al abrir el modal: conectar si hace falta y esperar (no alarmar si está reconectando).
  useEffect(() => {
    if (!isOpen) return undefined;
    const token = localStorage.getItem('token');
    if (!token) return undefined;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 90;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const r = await fetch('/api/whatsapp/estado', {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
        });
        const data = await r.json();
        if (!data.ok || !data.data) return;
        const d = data.data;
        const connected = d.status === 'connected' && Boolean(d.phone);
        const reconectando =
          !connected &&
          d.hasSavedOnDisk &&
          (d.reconnectingFromSaved ||
            d.phone ||
            ['connecting', 'saved'].includes(d.status) ||
            /Timed out after waiting|chrome_init_timeout|saturado|ocupado arrancando/i.test(
              `${d.error || ''} ${d.lastDisconnectReason || ''}`
            ));

        if (connected) {
          setWaSesionOk(true);
          setWaSesionAviso('');
          return;
        }

        // Con QR en pantalla / desvinculada: no relanzar (candado; solo «Generar nuevo QR»).
        const qrCandado =
          Boolean(d.qrBase64) ||
          Boolean(d.manualQrMode) ||
          Boolean(d.needsQrScan) ||
          Boolean(d.desvinculada) ||
          /desvinculada|LOGOUT/i.test(String(d.lastDisconnectReason || ''));

        if (reconectando) {
          setWaSesionOk(false);
          setWaSesionAviso(
            /saturado|EAGAIN|ocupado/i.test(`${d.error || ''} ${d.statusMessage || ''}`)
              ? 'WhatsApp reconectando (el servidor estaba ocupado). Espera unos segundos…'
              : 'Conectando tu WhatsApp…'
          );
          if (attempts === 1 && !qrCandado) kickWhatsappReconnect(token);
          if (attempts < maxAttempts) {
            setTimeout(poll, 2000);
          }
          return;
        }

        setWaSesionOk(false);
        setWaSesionAviso(
          d.statusMessage ||
            d.error ||
            'WhatsApp no conectado. Abre Configuración → WhatsApp o espera un momento.'
        );
        if (attempts === 1) kickWhatsappReconnect(token);
      } catch {
        /* ignore */
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    try {
      const u = JSON.parse(localStorage.getItem('usuario') || '{}');
      setAgentePrimerNombre(primerNombre(u.nombre || ''));
    } catch {
      setAgentePrimerNombre('');
    }
    api.get('/auth/me').then((r) => {
      const n = r.data?.nombre;
      if (n) setAgentePrimerNombre(primerNombre(n));
    }).catch(() => {});
  }, [isOpen]);

  const [quickRepliesFromApi, setQuickRepliesFromApi] = useState(null);

  useEffect(() => {
    if (!isOpen || customReplies) {
      setQuickRepliesFromApi(null);
      return;
    }
    let cancelled = false;
    const count = propsAdjuntas.length;
    const videosCount =
      videoSeleccion === 'todos'
        ? videosCatalog.length
        : (videoSeleccion === 'ninguno' || !videoSeleccion
          ? 0
          : (videosCatalog.some((v) => v.id === videoSeleccion) ? 1 : 0));
    const idiomaWa = normalizeWaIdioma(idioma);
    api
      .get('/whatsapp-plantillas/runtime', { params: { count, videos: videosCount, idioma: idiomaWa } })
      .then((r) => {
        if (cancelled) return;
        if (r.data?.ok && Array.isArray(r.data.replies)) {
          setQuickRepliesFromApi(r.data.replies);
        } else {
          setQuickRepliesFromApi(buildWhatsAppQuickReplies(count, idiomaWa, videosCount));
        }
      })
      .catch(() => {
        if (!cancelled) setQuickRepliesFromApi(buildWhatsAppQuickReplies(count, idiomaWa, videosCount));
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, customReplies, propsAdjuntas.length, idioma, videoSeleccion, videosCatalog.length]);

  // ── Drag del modal ───────────────────────────────────────────
  const [pos, setPos] = useState(null);
  const dragRef = useRef(null);
  const dragOffsetRef = useRef(null);
  const startDrag = (e) => {
    if (e.target.closest('button')) return;
    const rect = dragRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragOffsetRef.current = { ox: e.clientX - rect.left, oy: e.clientY - rect.top, w: rect.width, h: rect.height };
    document.body.style.userSelect = 'none';
    const onMove = (ev) => {
      if (!dragOffsetRef.current) return;
      const { ox, oy, w, h } = dragOffsetRef.current;
      setPos({ x: Math.max(0, Math.min(window.innerWidth - w, ev.clientX - ox)), y: Math.max(0, Math.min(window.innerHeight - h, ev.clientY - oy)) });
    };
    const onUp = () => { dragOffsetRef.current = null; document.body.style.userSelect = ''; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  if (!isOpen) return null;

  const idiomaWa = normalizeWaIdioma(idioma);
  const videosCountQr = countVideosSeleccionados();
  const quickRepliesList =
    customReplies
    || quickRepliesFromApi
    || buildWhatsAppQuickReplies(propsAdjuntas.length, idiomaWa, videosCountQr);
  const hasProps = propsAdjuntas.length > 0;

  const removeProp = (id) => {
    setPropsAdjuntas(prev => prev.filter(p => p.id !== id));
  };

  const nombreClienteParaPlantilla = () => {
    const raw = allowManualRecipient ? manualNombre.trim() : String(contact?.nombre || '').trim();
    // Solo nombre real de pila; Particular / Contacto / etc. → vacío (como el Saludo de captación).
    return nombreParaPlantillaMensaje(raw);
  };

  const mensajeConPlaceholdersResuelto = () =>
    aplicarPlaceholdersMensaje(mensaje, nombreClienteParaPlantilla(), agentePrimerNombre);

  const applyQuickReply = (text) => {
    setMensaje(
      aplicarPlaceholdersMensaje(text, nombreClienteParaPlantilla(), agentePrimerNombre).slice(0, 1000),
    );
  };

  const nombreDestinatario = allowManualRecipient ? (manualNombre.trim() || 'Destinatario') : (contact?.nombre || '');
  const telefonoEfectivo = allowManualRecipient
    ? manualTelefono.replace(/\D/g, '')
    : String(contact?.telefono || '').replace(/\D/g, '');

  // ── Construir mensaje completo con propiedades ──
  const buildFullMessage = () => {
    let msg = mensajeConPlaceholdersResuelto() || '';

    if (hasProps) {
      // Si hay propiedades, añadirlas al mensaje
      const propsText = propsAdjuntas.map((p, i) => {
        return `\n🏠 *${p.titulo}*\n${p.detalle}\n💰 ${p.precio}`;
      }).join('\n');

      if (msg) {
        msg = msg + '\n' + propsText;
      } else {
        const pila = nombreParaPlantillaMensaje(nombreDestinatario);
        msg = (pila ? `Hola ${pila},` : 'Hola,') +
          ' te envío estas propiedades que creo pueden interesarte:' + propsText;
      }
    }

    return msg;
  };

  const handleSend = async () => {
    const phone = telefonoEfectivo;
    const hasProps = propsAdjuntas.length > 0;
    const mensajeEnviar = mensajeConPlaceholdersResuelto().slice(0, 1000);
    const soloTarjeta = !hasProps && !mensajeEnviar.trim() && enviarTarjeta && !!tarjetaUrl;

    if (allowManualRecipient) {
      if (!phone || phone.length < 9) {
        setErrorEnvio('Indica un número de teléfono válido (con prefijo si hace falta)');
        return;
      }
    } else if (!phone) {
      setErrorEnvio('El contacto no tiene teléfono');
      return;
    }

    if (!hasProps && !mensajeEnviar.trim() && !(enviarTarjeta && tarjetaUrl)) {
      setErrorEnvio('Escribe un mensaje o activa la tarjeta de visita');
      return;
    }

    setEnviando(true);
    setErrorEnvio('');

    const crmPayload = {};
    if (crmContext?.lead_id) crmPayload.lead_id = crmContext.lead_id;
    if (crmContext?.cliente_id) crmPayload.cliente_id = crmContext.cliente_id;
    if (crmContext?.captacion_id) crmPayload.captacion_id = crmContext.captacion_id;

    try {
      const token = localStorage.getItem('token');

      const handleWaFailure = async (data) => {
        const shouldExtendDeadline = data.needsConnection && !data.canRetry;
        if (shouldExtendDeadline && crmContext?.lead_id) {
          await api.post(`/tareas/lead/${crmContext.lead_id}/whatsapp-bloqueado`, {
            reason: 'sin_sesion',
            extend_minutes: LEAD_FLOW_TIMING.whatsappWaitExtendMinutes || LEAD_FLOW_TIMING.defaultTaskDueMinutes || 240,
          }).catch(() => {});
        }
        const msgErr = mensajeErrorWhatsapp(data);
        setErrorEnvio(msgErr);
        if (hasProps) {
          mostrarToastEnvioPropiedades({
            ok: false,
            canal: 'whatsapp',
            count: propsAdjuntas.length,
            mensaje: msgErr,
          });
        } else {
          mostrarToastVitalCasa('No se ha podido enviar el WhatsApp', {
            tipo: 'error',
            detalle: msgErr,
            duracionMs: 2800,
          });
        }
      };

      if (hasProps) {
        const data = await enviarWaConReintento({
          url: '/api/whatsapp/enviar-propiedades',
          token,
          options: {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              telefono: phone,
              mensaje_intro: mensajeEnviar || null,
              idioma: idiomaWa,
              ...crmPayload,
              propiedades_ids: propsAdjuntas.map((p) => p.id).filter(Boolean),
              propiedades: propsAdjuntas.map(p => ({
                id: p.id,
                titulo: p.titulo,
                tipo: p.tipo || '',
                poblacion: p.poblacion || '',
                zona: p.zona || '',
                precio: p.precio,
                operacion: p.operacion || '',
                habitaciones: p.habitaciones || null,
                banos: p.banos || null,
                m2: p.m2 || null,
                referencia: p.referencia || '',
                descripcion: p.descripcion || '',
              })),
              video_envio: videoEnvioPayload(),
            }),
          },
        });

        if (!data.ok) {
          await handleWaFailure(data);
          return;
        }
      } else if (!soloTarjeta) {
        const data = await enviarWaConReintento({
          url: '/api/whatsapp/enviar',
          token,
          options: {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              telefono: phone,
              mensaje: mensajeEnviar,
              ...crmPayload,
              ...(propsAdjuntas.length > 0
                ? { propiedades_ids: propsAdjuntas.map((p) => p.id).filter(Boolean) }
                : {}),
            }),
          },
        });

        if (!data.ok) {
          await handleWaFailure(data);
          return;
        }
      }

      // Enviar tarjeta de visita si el agente lo ha activado
      if (enviarTarjeta && tarjetaUrl) {
        try {
          if (!soloTarjeta) {
            await new Promise(r => setTimeout(r, 800)); // pausa si va tras texto/fichas
          }
          const token = localStorage.getItem('token');
          const tarjetaRes = await fetchJsonWa('/api/whatsapp/enviar-tarjeta', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ telefono: phone, tarjeta_url: tarjetaUrl }),
          });
          if (!tarjetaRes?.ok) {
            throw new Error(tarjetaRes?.error || 'Error enviando tarjeta');
          }
        } catch (e) {
          console.warn('[WA] No se pudo enviar la tarjeta de visita:', e.message);
          if (soloTarjeta) {
            setErrorEnvio(e.message || 'No se pudo enviar la tarjeta de visita');
            mostrarToastVitalCasa('No se ha podido enviar la tarjeta', {
              tipo: 'error',
              detalle: e.message,
              duracionMs: 2800,
            });
            return;
          }
          // Con mensaje/fichas ya enviados: no bloqueamos el flujo principal
        }
      }

      // Toast fuera de React: el onSend del padre suele desmontar este modal al instante.
      if (hasProps) {
        mostrarToastEnvioPropiedades({
          ok: true,
          canal: 'whatsapp',
          count: propsAdjuntas.length,
        });
      } else {
        mostrarToastVitalCasa('WhatsApp enviado correctamente', { tipo: 'ok', duracionMs: 2000 });
      }

      // Callback al padre (historial CRM / etapa). Await para que el padre persista antes de cerrar.
      try {
        await onSend?.({
          contacto: allowManualRecipient ? { ...contact, nombre: nombreDestinatario, telefono: phone } : contact,
          propiedades: propsAdjuntas,
          mensaje: mensajeEnviar,
        });
      } catch (cbErr) {
        console.warn('[WA] onSend callback:', cbErr?.message || cbErr);
      }

      setEnviado(true);
      setTimeout(() => {
        setEnviado(false);
        setMensaje('');
        onClose?.();
      }, 1800);
    } catch (e) {
      console.error('Error enviando WhatsApp:', e);
      const msgErr = e?.message || 'No se pudo conectar con el servidor';
      setErrorEnvio(msgErr);
      if (hasProps) {
        mostrarToastEnvioPropiedades({
          ok: false,
          canal: 'whatsapp',
          count: propsAdjuntas.length,
          mensaje: msgErr,
        });
      } else {
        mostrarToastVitalCasa('No se ha podido enviar el WhatsApp', {
          tipo: 'error',
          detalle: msgErr,
          duracionMs: 2800,
        });
      }
    } finally {
      setEnviando(false);
    }
  };

  // ── RENDER ──────────────────────────────────────
  // Portal a document.body: evita que layouts con overflow:hidden (p. ej. FichaCliente)
  // recorten el overlay y oculten respuestas rápidas / vídeos.
  return createPortal(
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 10000, fontFamily: "'DM Sans', sans-serif",
    }}>
      <div ref={dragRef} onClick={e => e.stopPropagation()} style={{
        width: 480, maxHeight: 'min(94vh, 780px)', display: 'flex', flexDirection: 'column',
        background: isDark ? c.bgCard : '#c8c8be', borderRadius: 16, overflow: 'hidden',
        border: `1px solid ${isDark ? c.border : '#a8a8a0'}`,
        boxShadow: isDark
          ? '0 0 60px 15px rgba(255,255,255,0.18), 0 0 120px 40px rgba(255,255,255,0.10), 0 0 200px 80px rgba(255,255,255,0.05)'
          : '0 16px 48px rgba(0,0,0,0.30), 0 4px 12px rgba(0,0,0,0.15)',
        position: pos ? 'fixed' : 'relative',
        ...(pos ? { left: pos.x, top: pos.y, margin: 0 } : {}),
      }}>

        {/* Confirmación de envío */}
        {enviado && (
          <div style={{
            position: 'absolute', inset: 0, borderRadius: 14, background: c.bgCard,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 12, zIndex: 2,
          }}>
            <div style={{ fontSize: 48 }}>✅</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: wa.green }}>
              {propsAdjuntas.length > 1
                ? 'Propiedades enviadas'
                : propsAdjuntas.length === 1
                  ? 'Propiedad enviada'
                  : 'WhatsApp enviado'}
            </div>
            <div style={{ fontSize: 13, color: c.textMuted, fontWeight: 500 }}>
              Mensaje entregado a {nombreDestinatario}
              {enviarTarjeta && <><br /><span style={{ fontSize: 12, color: wa.green }}>💼 Tarjeta de visita enviada</span></>}
            </div>
          </div>
        )}

        {/* Barra verde superior */}
        <div style={{
          height: 3, flexShrink: 0,
          background: `linear-gradient(90deg, ${wa.greenLight}, ${wa.green}, ${wa.greenFrom}, ${wa.green}, ${wa.greenLight})`,
        }} />

        {/* Header */}
        <div onMouseDown={startDrag} style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, cursor: 'grab', userSelect: 'none', background: isDark ? 'transparent' : '#e0e0d8', borderBottom: `1px solid ${isDark ? c.border : '#a8a8a0'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: `linear-gradient(135deg, ${wa.greenFrom}, ${wa.greenTo})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, border: `1px solid ${c.border}`,
              boxShadow: `0 4px 12px ${wa.greenShadow}`,
            }}>💬</div>
            <div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: c.textPrimary }}>Enviar WhatsApp</h2>
              <p style={{ margin: 0, marginTop: 1, fontSize: 12, fontWeight: 500, color: c.textMuted }}>
                {hasProps ? `${propsAdjuntas.length} propiedad${propsAdjuntas.length > 1 ? 'es' : ''} seleccionada${propsAdjuntas.length > 1 ? 's' : ''}` : 'Mensaje directo'}
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{
            width: 32, height: 32, borderRadius: 8, border: `1px solid ${c.border}`,
            background: c.bgInput, cursor: 'pointer', fontSize: 16, color: c.textMuted,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>✕</button>
        </div>

        {/* Cuerpo scrollable */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: isDark ? 0 : '2px 0 4px' }}>

          {/* Destinatario */}
          <div style={{ margin: '6px 10px 0', padding: '8px 12px', background: isDark ? 'transparent' : '#ffffff', borderRadius: 10, border: isDark ? 'none' : `1px solid #c8c8be`, borderLeft: isDark ? 'none' : `4px solid ${c.accentBlue}`, boxShadow: isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: isDark ? c.textMuted : c.accentBlue }}>Destinatario</span>
            </div>
            {allowManualRecipient ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input
                  type="text"
                  placeholder="Nombre (opcional)"
                  value={manualNombre}
                  onChange={e => setManualNombre(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: 10,
                    border: `2px solid ${c.border}`, background: c.bgInput, color: c.textPrimary,
                    fontSize: 14, fontFamily: 'inherit', outline: 'none',
                  }}
                />
                <input
                  type="tel"
                  placeholder="Teléfono WhatsApp (ej. 34600111222)"
                  value={manualTelefono}
                  onChange={e => setManualTelefono(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: 10,
                    border: `2px solid ${wa.greenBorder}`, background: c.bgInput, color: c.textPrimary,
                    fontSize: 14, fontFamily: 'inherit', outline: 'none',
                  }}
                />
              </div>
            ) : (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 10,
                border: `1px solid ${c.border}`,
                borderLeft: isDark ? `1px solid ${c.border}` : `3px solid ${c.accentBlue}`,
                background: isDark ? c.bgInput : c.bgCard,
                boxShadow: isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.05)',
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: `linear-gradient(135deg, ${c.accentBlue}, ${c.accentBlueDark})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0,
                }}>{contact?.iniciales || '??'}</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: c.textPrimary }}>{contact?.nombre || 'Sin nombre'}</div>
                  <div style={{ fontSize: 12, color: c.textMuted, fontWeight: 500, marginTop: 1 }}>{contact?.telefono || 'Sin teléfono'}</div>
                </div>
              </div>
            )}
          </div>

          {/* Propiedades adjuntas */}
          {hasProps && (
            <div style={{ margin: '6px 10px 0', padding: '8px 12px', background: isDark ? 'transparent' : '#ffffff', borderRadius: 10, border: isDark ? 'none' : `1px solid #c8c8be`, borderLeft: isDark ? 'none' : `4px solid ${wa.green}`, boxShadow: isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.08)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
                <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: isDark ? c.textMuted : wa.green }}>Propiedades adjuntas</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {propsAdjuntas.map(prop => (
                  <div key={prop.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '7px 10px', borderRadius: 8,
                    background: isDark ? c.bgInput : 'rgba(22,163,74,0.16)',
                    border: `1px solid ${isDark ? c.border : 'rgba(22,163,74,0.40)'}`,
                    borderLeft: `4px solid ${wa.green}`,
                  }}>
                    <div style={{
                      width: 48, height: 36, borderRadius: 6,
                      background: c.bgCard, flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 16, color: c.textMuted, border: `1px solid ${c.borderLight}`,
                    }}>{prop.icono || '🏠'}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: c.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{prop.titulo}</div>
                      <div style={{ fontSize: 11.5, color: c.textMuted, fontWeight: 500, marginTop: 2 }}>
                        {(prop.referencia || prop.ref) ? `Ref. ${String(prop.referencia || prop.ref).trim()}` : null}
                      </div>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: wa.green, flexShrink: 0 }}>{prop.precio}</div>
                    <div onClick={() => removeProp(prop.id)} style={{
                      width: 24, height: 24, borderRadius: 6,
                      border: `1px solid ${c.border}`, background: c.bgInput,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, color: c.textMuted, cursor: 'pointer', flexShrink: 0,
                    }}>✕</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mensaje */}
          <div style={{ margin: '6px 10px 0', padding: '8px 12px', background: isDark ? 'transparent' : '#ffffff', borderRadius: 10, border: isDark ? 'none' : `1px solid #c8c8be`, borderLeft: isDark ? 'none' : `4px solid ${wa.green}`, boxShadow: isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: isDark ? c.textMuted : wa.green }}>Mensaje</span>
            </div>
            <textarea
              className="modal-wa-textarea"
              style={{
                width: '100%', minHeight: hasProps ? 52 : 72, maxHeight: hasProps ? 90 : 120,
                padding: '8px 12px', borderRadius: 8,
                border: `1.5px solid ${msgFocused ? wa.green : (isDark ? c.border : '#a8a8a0')}`,
                background: isDark ? c.bgInput : 'rgba(22,163,74,0.16)', color: c.textPrimary,
                fontSize: 13.5, fontWeight: 500, fontFamily: 'inherit',
                outline: 'none', resize: hasProps ? 'none' : 'vertical', transition: 'border-color 0.2s',
              }}
              placeholder={
                hasProps
                  ? 'Escribe un mensaje personalizado (opcional)...'
                  : (enviarTarjeta
                    ? 'Mensaje opcional — puedes enviar solo la tarjeta...'
                    : 'Escribe tu mensaje...')
              }
              value={mensaje}
              onChange={e => setMensaje(e.target.value.slice(0, 1000))}
              onFocus={() => setMsgFocused(true)}
              onBlur={() => setMsgFocused(false)}
            />
            <div style={{ textAlign: 'right', fontSize: 10, color: c.textMuted, fontWeight: 500, marginTop: 4, opacity: 0.5 }}>
              {mensaje.length} / 1.000
            </div>

            {/* Respuestas rápidas (mensaje directo o con propiedades: el intro se envía y luego el bloque de fichas) */}
            <>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: c.textMuted, marginTop: 6, marginBottom: 4, opacity: 0.6 }}>
                Respuestas rápidas
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {quickRepliesList.map(qr => (
                  <button key={qr.id} type="button" onClick={() => applyQuickReply(qr.text)} style={{
                    padding: '4px 8px', borderRadius: 5,
                    border: `1px solid ${wa.greenBorder}`, background: wa.greenBg,
                    color: wa.chipText, fontSize: 10, fontWeight: 600,
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}>{qr.emoji} {qr.label}</button>
                ))}
              </div>
            </>
          </div>

          {/* Tarjeta de visita */}
          <div style={{ margin: '6px 10px 0', padding: 0 }}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 10px', borderRadius: 8,
              border: `1px solid ${enviarTarjeta ? wa.greenBorder : c.border}`,
              background: enviarTarjeta ? wa.greenBg : (isDark ? 'rgba(255,255,255,0.02)' : '#f9fafb'),
              cursor: tarjetaUrl ? 'pointer' : 'not-allowed',
              opacity: tarjetaUrl ? 1 : 0.45,
              transition: 'all 0.2s',
              userSelect: 'none',
            }}>
              <input
                type="checkbox"
                checked={enviarTarjeta}
                disabled={!tarjetaUrl}
                onChange={e => setEnviarTarjeta(e.target.checked)}
                style={{ width: 16, height: 16, accentColor: wa.green, cursor: tarjetaUrl ? 'pointer' : 'not-allowed', flexShrink: 0 }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: enviarTarjeta ? wa.green : c.textPrimary, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>💼</span>
                  {agenteTarjetaUrl ? 'Adjuntar tarjeta del agente' : 'Adjuntar mi tarjeta de visita'}
                  {enviarTarjeta && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4, background: wa.greenBorder, color: wa.chipText }}>2.º</span>}
                </div>
                <div style={{ fontSize: 10, color: c.textMuted, marginTop: 1 }}>
                  {tarjetaUrl
                    ? (agenteTarjetaUrl
                      ? 'Tarjeta del agente asignado a esta captación'
                      : 'Puedes enviarla sola o junto con un mensaje / fichas')
                    : 'El agente asignado no tiene tarjeta de visita en su perfil'}
                </div>
              </div>
              {tarjetaUrl && (
                <img
                  src={tarjetaUrl}
                  alt="tarjeta"
                  style={{ width: 44, height: 28, objectFit: 'contain', borderRadius: 4, border: `1px solid ${c.border}`, flexShrink: 0, background: '#fff' }}
                />
              )}
            </label>
          </div>

          {/* Vídeos de ficha (tras tarjeta): no enviar | uno | todos */}
          {hasProps && (videosLoading || videosCatalog.length > 0) && (
            <div style={{
              margin: '4px 10px 0',
              padding: '6px 10px',
              borderRadius: 8,
              border: `1px solid ${wa.greenBorder}`,
              background: wa.greenBg,
            }}>
              <div style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: c.textMuted, marginBottom: 4 }}>
                Vídeos en el envío
              </div>
              {videosLoading ? (
                <div style={{ fontSize: 11, color: c.textMuted }}>Cargando…</div>
              ) : (
                <select
                  value={videoSeleccion}
                  onChange={(e) => setVideoSeleccion(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '5px 8px',
                    borderRadius: 6,
                    border: `1px solid ${c.border}`,
                    background: c.bgInput,
                    color: c.textPrimary,
                    fontSize: 11,
                    fontFamily: 'inherit',
                  }}
                >
                  <option value="ninguno">No incluir vídeos</option>
                  <option value="todos">Todos los vídeos</option>
                  {videosCatalog.map((v) => (
                    <option key={v.id} value={v.id}>{v.label}</option>
                  ))}
                </select>
              )}
            </div>
          )}

          <div style={{ height: 4 }} />
        </div>
{/* Aviso sesión WA desconectada (antes de enviar) */}
        {!waSesionOk && waSesionAviso && !errorEnvio && (
          <div style={{ padding: '0 24px 8px', fontSize: 12, color: '#b45309', fontWeight: 600, textAlign: 'center', lineHeight: 1.45 }}>
            ⚠️ {waSesionAviso}
          </div>
        )}
        {errorEnvio && (
          <div style={{ padding: '0 24px 8px', fontSize: 12, color: '#ef4444', fontWeight: 600, textAlign: 'center' }}>
            <div>⚠️ {errorEnvio}</div>
            {/no conectado|qr|escanea/i.test(errorEnvio) && (
              <div style={{ fontSize: 11, fontWeight: 500, color: c.textMuted, marginTop: 6, lineHeight: 1.4 }}>
                Si ya envías con normalidad, reintenta en unos segundos (el servidor puede estar reconectando la sesión). Si persiste, abre Configuración → WhatsApp y comprueba el estado o vuelve a escanear el QR.
              </div>
            )}
          </div>
        )}
        {/* Footer */}
        <div style={{
          padding: '10px 14px 12px',
          display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center',
          flexShrink: 0, borderTop: `1px solid ${isDark ? c.border : '#a8a8a0'}`,
          background: isDark ? 'transparent' : '#e0e0d8',
        }}>
          <button onClick={onClose} style={{
            padding: '11px 24px', borderRadius: 10,
            border: `1.5px solid ${isDark ? c.border : '#a8a8a0'}`,
            background: isDark ? c.bgInput : '#ffffff',
            color: c.textPrimary,
            cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
          }}>Cancelar</button>
          <button onClick={handleSend} disabled={enviando} style={{
            padding: '11px 30px', borderRadius: 10, border: 'none',
            background: `linear-gradient(135deg, ${wa.greenFrom}, ${wa.greenTo})`,
            color: wa.greenText, cursor: 'pointer', fontFamily: 'inherit',
            fontSize: 13, fontWeight: 700,
            boxShadow: `0 4px 14px ${wa.greenShadow}`,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span style={{ fontSize: 15 }}>📨</span>
            {enviando ? 'Enviando...' : 'Enviar WhatsApp'}
          </button>
        </div>

      </div>
    </div>,
    document.body
  );
}
